import React, { createContext, useContext } from 'react';
import { useQuery, UseQueryResult } from '@edreamsodigeo/frontend-api-client';

import { useSearchQuery } from '../SearchRequestProvider';
import { ITINERARY_SEARCH_QUERY } from '../operations/itinerarySearch';
import {
  useCreateMapiShoppingCart,
  useTrackErrors,
  useUpdateMeshTransactionId,
  useLoadingResultsPage,
  useSkipQueryMetric,
} from './hooks';
import FrontendAPIErrorManager from 'clientState/errorManagement';
import {
  ItinerarySearchQuery,
  SearchQueryQuery,
  SearchQueryQueryVariables,
  SearchRequest,
} from 'generated/graphql';
import { useEndSearchMetricEffect } from '../SearchTrackerProvider';
import {
  createFeature,
  useFrontendApiBlockingError,
} from '@edreamsodigeo/frontend-core';
import { transformSuccessQuery } from '../transformSuccessQuery';
import { transformItinerarySearchResponse } from './transformItinerarySearchResponse';
import { Extensions } from 'clientState/resolvers/itinerary';
import { useFlowTools } from 'flowTools/useFlowTools';
import ItinerarySearchResponseNoShoppingCartProvider from './ItinerarySearchResponseNoShoppingCartProvider';
import {
  META_LANDING_IN_OF2_MOBILE,
  META_LANDING_IN_OF2_DESKTOP,
} from 'clientState/ab/aliases';
import { useAlternativeItineraryMutation } from 'clientState/providers/AlternativeItineraryMutationProvider';
import { useHash } from 'clientState/hooks/useUrlParams';

export type SearchResponseProviderProps = {
  onNoSearchResults: (event: MouseEvent) => void;
  onSearchParamsError: () => void;
  onCreateMapiShoppingCart: (
    data: SearchQueryQuery
  ) => Promise<{ redirect: boolean }>;
  onSearchMapiError: () => void;
  onLoadingResultsPage: () => void;
};
export type SearchQueryResult = UseQueryResult<
  SearchQueryQuery,
  SearchQueryQueryVariables
>;
export type SearchResponseContextType = {
  loading: boolean;
  data: SearchQueryResult['data'];
  error: SearchQueryResult['errors'];
  extensions: SearchQueryResult['extensions'];
};
type SearchResponseActionsContextType = Pick<
  SearchResponseProviderProps,
  'onNoSearchResults' | 'onSearchParamsError'
>;

const LOADING_STATE = {
  loading: true,
  data: null,
  error: null,
  extensions: null,
};
const DEFAULT_ACTIONS = {
  onNoSearchResults: /* istanbul ignore next */ (): void => {},
  onSearchParamsError: /* istanbul ignore next */ (): void => {},
};

export const SearchResponseContext =
  createContext<SearchResponseContextType>(LOADING_STATE);
export const SearchResponseActionsContext =
  createContext<SearchResponseActionsContextType>(DEFAULT_ACTIONS);

export const replaceItinerariesById = (
  itineraries,
  alternativesItineraries
) => {
  if (!alternativesItineraries || alternativesItineraries.length === 0) {
    return itineraries;
  }
  return itineraries.map((itinerary) => {
    const match = alternativesItineraries.find(
      (newItem) => newItem.id === itinerary.id
    );
    return match ? match : itinerary;
  });
};

export const ItinerarySearchResponseProvider: React.FC<
  SearchResponseProviderProps
> = ({
  onNoSearchResults,
  onSearchParamsError,
  children,
  onCreateMapiShoppingCart,
  onSearchMapiError,
  onLoadingResultsPage,
}) => {
  const searchRequest = useSearchQuery();
  const hash = useHash();
  const {
    alternativeMutationLaunched,
    alternativeSearchId,
    alternativeItineraries,
  } = useAlternativeItineraryMutation();
  const isNotAllowedHash =
    hash && !(hash.includes('#results') || hash.includes('#/results'));
  const skipQuery =
    searchRequest === null ||
    alternativeMutationLaunched ||
    alternativeSearchId !== null ||
    !!isNotAllowedHash;
  useSkipQueryMetric(
    skipQuery,
    searchRequest,
    alternativeMutationLaunched,
    alternativeSearchId,
    isNotAllowedHash
  );
  const queryItinerarySearchResult = useQuery<
    ItinerarySearchQuery,
    SearchQueryQueryVariables
  >(ITINERARY_SEARCH_QUERY, {
    variables: {
      searchRequest: searchRequest as SearchRequest,
    },
    skip: skipQuery,
    notifyOnBackgroundFetching: true,
  });
  const { loading: loadingMapiShoppingCart } = useCreateMapiShoppingCart(
    onCreateMapiShoppingCart,
    onSearchMapiError,
    searchRequest,
    queryItinerarySearchResult.data
  );
  useFrontendApiBlockingError(queryItinerarySearchResult);

  const itineraries =
    queryItinerarySearchResult.data?.itinerarySearch?.itineraries || [];
  const itinerariesWithAlternatives = replaceItinerariesById(
    itineraries,
    alternativeItineraries
  );

  if (queryItinerarySearchResult.data) {
    queryItinerarySearchResult.data.itinerarySearch.itineraries =
      itinerariesWithAlternatives;
  }

  const searchQueryState = transformSuccessQuery(
    {
      loading:
        queryItinerarySearchResult.loading ||
        queryItinerarySearchResult.fetching,
      error: queryItinerarySearchResult.errors,
      data: queryItinerarySearchResult.data,
      extensions: queryItinerarySearchResult.extensions,
    },
    transformItinerarySearchResponse,
    {}
  );
  const { loading: loadingSearch, data, error } = searchQueryState;

  const loading =
    loadingSearch ||
    loadingMapiShoppingCart ||
    searchRequest === null ||
    queryItinerarySearchResult.fetching ||
    (!error && queryItinerarySearchResult.data === null);
  const extensions = searchQueryState?.extensions;

  useEndSearchMetricEffect(queryItinerarySearchResult);
  useTrackErrors(data, loading, queryItinerarySearchResult.errors);
  useUpdateMeshTransactionId(data);
  useLoadingResultsPage(loading, onLoadingResultsPage);
  const nextQueryState = React.useMemo(
    () => ({
      loading,
      data: loading ? null : data,
      error,
      extensions: extensions as Extensions | null,
    }),
    [loading, data, error, extensions]
  );

  const actions = {
    onNoSearchResults,
    onSearchParamsError,
  };

  return (
    <>
      <FrontendAPIErrorManager
        errors={queryItinerarySearchResult.errors}
        id="SearchResponseProvider"
      />
      <SearchResponseContext.Provider value={nextQueryState}>
        <SearchResponseActionsContext.Provider value={actions}>
          {children}
        </SearchResponseActionsContext.Provider>
      </SearchResponseContext.Provider>
    </>
  );
};

export const useSearchResponseQuery = (): SearchResponseContextType =>
  useContext(SearchResponseContext);

export const useSearchResponseActions = (): SearchResponseActionsContextType =>
  useContext(SearchResponseActionsContext);

export const useDefaultFeeType = ():
  | SearchQueryQuery['search']['defaultFeeType']
  | null => {
  const { error, loading, data } = useSearchResponseQuery();
  if (error || loading) {
    return null;
  }
  return data?.search.defaultFeeType;
};

export const useTrackingInfo = ():
  | SearchQueryQuery['search']['trackingInfo']
  | null => {
  const { error, loading, data } = useSearchResponseQuery();
  if (error || loading) {
    return null;
  }
  return data?.search.trackingInfo;
};

export const useSearchId = ():
  | SearchQueryQuery['search']['searchId']
  | null => {
  const { loading, error, data } = useSearchResponseQuery();
  const { alternativeSearchId } = useAlternativeItineraryMutation();
  if (loading || error) {
    return null;
  }
  return alternativeSearchId || data?.search.searchId || null;
};

const useCampaignItineraryIds = (campaignId: string): string[] => {
  const { loading, error, data } = useSearchResponseQuery();
  if (loading || error) {
    return [];
  }

  return (
    data?.search.campaignsItineraryLists?.find(
      (campaign) => campaign.campaignKey === campaignId
    )?.campaignItineraryIds || []
  );
};

const HOTEL_VOUCHER_CAMPAIGN_ID = 'hotel-voucher';
export const useHotelVoucherItineraryIds = (): string[] =>
  useCampaignItineraryIds(HOTEL_VOUCHER_CAMPAIGN_ID);

const FLIGHT_OF_THE_DAY_CAMPAIGN_ID = 'flight-of-the-day';
export const useFlightOfTheDayItineraryIds = (): string[] =>
  useCampaignItineraryIds(FLIGHT_OF_THE_DAY_CAMPAIGN_ID);

const ItinerarySearchResponseMobileProvider = createFeature({
  name: META_LANDING_IN_OF2_MOBILE,
  variations: [
    ItinerarySearchResponseProvider,
    ItinerarySearchResponseNoShoppingCartProvider,
  ],
  LoadingComponent: () => null,
  version: 1,
});
const ItinerarySearchResponseDesktopProvider = createFeature({
  name: META_LANDING_IN_OF2_DESKTOP,
  variations: [
    ItinerarySearchResponseProvider,
    ItinerarySearchResponseNoShoppingCartProvider,
  ],
  LoadingComponent: () => null,
  version: 1,
});

const ItinerarySearchResponseProviderVersioned: React.FC<
  SearchResponseProviderProps
> = (props) => {
  const { isMobile } = useFlowTools();
  const searchRequest = useSearchQuery();
  const isMeta = searchRequest?.itinerary?.externalSelection;

  if (!isMeta) {
    return <ItinerarySearchResponseProvider {...props} />;
  } else {
    return isMobile ? (
      <ItinerarySearchResponseMobileProvider {...props} />
    ) : (
      <ItinerarySearchResponseDesktopProvider {...props} />
    );
  }
};

export default ItinerarySearchResponseProviderVersioned;
